<template>
    <div id="app">
        <router-view />

        <!-- 飞行安全全屏弹窗 -->
        <el-dialog
            :visible.sync="safetyNoticeVisible"
            width="420px"
            :show-close="false"
            :close-on-click-modal="false"
            :close-on-press-escape="false"
            custom-class="safety-notice-dialog"
            append-to-body
            class="el-dialog__center"
        >
            <div class="safety-notice">
                <div class="safety-notice__header">
                    <i class="el-icon-warning"></i>
                    <span>{{ safetyNotice.title }}</span>
                </div>
                <div class="safety-notice__content">{{ safetyNotice.content }}</div>
                <p class="safety-notice__tip">阅读并知悉后方可进入飞行控制操作。</p>
                <div class="safety-notice__footer">
                    <el-button
                        type="primary"
                        :disabled="safetyNotice.popupReadMode === 2 && safetyCountdown > 0"
                        @click="confirmSafetyNotice"
                    >
                        {{ safetyNotice.popupReadMode === 2 && safetyCountdown > 0 ? safetyCountdown + 's 后可确认' : safetyNotice.popupConfirmText }}
                    </el-button>
                </div>
            </div>
        </el-dialog>

        <!-- 全局告警弹窗（越界告警/靠近预警），和语音播报同一份判重逻辑，逐条弹出 -->
        <el-dialog
            :visible="!!currentAlarmPopup"
            width="420px"
            :show-close="false"
            :close-on-click-modal="false"
            :close-on-press-escape="false"
            custom-class="alarm-popup-dialog"
            append-to-body
            class="el-dialog__center"
            @closed="displayedAlarmPopup = null"
        >
            <div
                class="alarm-popup"
                v-if="displayedAlarmPopup"
            >
                <div
                    class="alarm-popup__header"
                    :class="displayedAlarmPopup.level"
                >
                    <i class="el-icon-warning"></i>
                    <span>{{ displayedAlarmPopup.title }}</span>
                </div>
                <div class="alarm-popup__content">{{ displayedAlarmPopup.message }}</div>
                <div
                    class="alarm-popup__more"
                    v-if="alarmPopupQueue.length > 1"
                >另有 {{ alarmPopupQueue.length - 1 }} 条待确认</div>
                <div class="alarm-popup__footer">
                    <el-button
                        type="primary"
                        @click="ackAlarmPopup"
                    >我已知晓</el-button>
                </div>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import tbwebsocket from "@/plugins/tbwebsocket.js";
import wsip from "@/plugins/wsconfig.js";
import { getBoundaryAlarmConfig } from "@/api/boundary-alarm";

// 语音"已播报"、弹窗"已展示"登记表都存 sessionStorage：每个标签页各自独立一份（新开一个标签页是空的，
// 会重新播报/弹出一次），但同一个标签页刷新页面时会保留（不会重复播报/弹出）——语音和弹窗用同一套存储语义，
// 只是各自的 key 分开，互不影响
const OVERRUN_ANNOUNCED_KEY = "overrunAlarmAnnounced";
const NEAR_ANNOUNCED_KEY = "nearAlarmAnnounced";
const OVERRUN_POPUP_SHOWN_KEY = "overrunAlarmPopupShown";
const NEAR_POPUP_SHOWN_KEY = "nearAlarmPopupShown";

function readAnnouncedIds(storageKey) {
    try {
        const arr = JSON.parse(sessionStorage.getItem(storageKey) || "[]");
        return Array.isArray(arr) ? arr : [];
    } catch (e) {
        return [];
    }
}

function writeAnnouncedIds(storageKey, ids) {
    try {
        sessionStorage.setItem(storageKey, JSON.stringify(ids));
    } catch (e) {}
}

export default {
    name: "App",

    data() {
        return {
            safetyNoticeVisible: false,
            safetyNotice: {
                id: null,
                title: '',
                content: '',
                popupConfirmText: '',
                popupReadMode: 1,
                popupReadSeconds: 0,
            },
            safetyCountdown: 0,
            _countdownTimer: null,
            // 告警语音开关，从 /enterprise/overrunAlarmConfig/get 拉取；未加载完成前保持 null（不播报），避免用错误的默认值播报
            alarmVoiceSwitch: {
                near: null,
                overrun: null,
            },
            // 告警弹窗队列：逐条弹出，点"我已知晓"后展示下一条
            alarmPopupQueue: [],
            // 弹窗里实际展示的内容：队列清空时 currentAlarmPopup 会立刻变 null，但对话框关闭动画还没播完，
            // 这里额外存一份，只在有新内容时更新，关闭动画期间继续显示最后一条，避免动画过程中内容先消失、弹窗高度先塌陷
            displayedAlarmPopup: null,
        }
    },

    computed: {
        currentAlarmPopup() {
            return this.alarmPopupQueue[0] || null;
        },
    },

    watch: {
        currentAlarmPopup(val) {
            if (val) this.displayedAlarmPopup = val;
        },
    },

    mounted() {
        window._heartbeatFn_ = this.loginStatus;
        window.$openGlobalWs = this.globalWs;
        window.$closeGlobalWs = this.closeWS;
    },

    methods: {
        loginStatus() {
            this.heart();
            window._heartbeat_ = setInterval(() => {
                this.heart();
            }, 5000);
        },

        heart() {
            this.$axios
                .get("/fcms/heartbeat")
                .then(() => {})
                .catch(() => {});
        },

        // 拉取告警与推送配置，只关心语音开关；靠近预警是否播报要看这里的 nearVoiceSwitch
        // 越界告警是企业版功能，非企业账号没有这个接口的权限，之前这里漏了这个判断
        loadAlarmVoiceConfig() {
            if (localStorage.getItem('UserType') !== 'enterprise') return
            getBoundaryAlarmConfig()
                .then((response) => {
                    const { state, data } = response.data;
                    if (state === 1 && data) {
                        this.alarmVoiceSwitch.near = data.nearVoiceSwitch;
                        this.alarmVoiceSwitch.overrun = data.overrunVoiceSwitch;
                    }
                })
                .catch(() => {});
        },

        // 全局越界告警提醒：语音、弹窗各自一份独立的判重登记表（都存 sessionStorage，每个标签页独立提示一次，
        // 同一个标签页刷新页面不会重复提示）。告警恢复后各自登记会移除对应 id，之后该设备再越界会重新触发播报和弹窗
        announceOverrunAlarms(list) {
            const currentIdSet = new Set(list.map((item) => item.alarmId));

            const spoken = readAnnouncedIds(OVERRUN_ANNOUNCED_KEY).filter((id) => currentIdSet.has(id));
            const spokenSet = new Set(spoken);

            const popupShown = readAnnouncedIds(OVERRUN_POPUP_SHOWN_KEY).filter((id) => currentIdSet.has(id));
            const popupShownSet = new Set(popupShown);

            list.forEach((item) => {
                if (!spokenSet.has(item.alarmId)) {
                    spokenSet.add(item.alarmId);
                    spoken.push(item.alarmId);
                    this.speakOverrunAlarm(item.deviceName);
                }
                if (!popupShownSet.has(item.alarmId)) {
                    popupShownSet.add(item.alarmId);
                    popupShown.push(item.alarmId);
                    this.alarmPopupQueue.push({
                        key: `overrun-${item.alarmId}`,
                        level: "danger",
                        title: "越界告警",
                        message: `设备【${item.deviceName}】已飞出电子围栏，请立即返航`,
                    });
                }
            });
            writeAnnouncedIds(OVERRUN_ANNOUNCED_KEY, spoken);
            writeAnnouncedIds(OVERRUN_POPUP_SHOWN_KEY, popupShown);
        },

        speakOverrunAlarm(deviceName) {
            try {
                if (!window.speechSynthesis) return;
                const utter = new SpeechSynthesisUtterance(`设备${deviceName}已飞出电子围栏，请立即返航`);
                utter.lang = "zh-CN";
                window.speechSynthesis.speak(utter);
            } catch (e) {}
        },

        // 靠近预警：页头下方只展示越界告警，靠近预警不展示 UI；nearVoiceSwitch 关闭时语音和弹窗都不提示。
        // 语音、弹窗各自一份独立的判重登记表（都存 sessionStorage），跟越界告警同一套思路。
        // 判重用 id（单条靠近预警的唯一标识），不用 sn（设备编号）——同一设备可能有多次独立的靠近预警
        announceNearAlarms(list) {
            if (this.alarmVoiceSwitch.near !== 1) return;

            const currentIdSet = new Set(list.map((item) => item.id));

            const spoken = readAnnouncedIds(NEAR_ANNOUNCED_KEY).filter((id) => currentIdSet.has(id));
            const spokenSet = new Set(spoken);

            const popupShown = readAnnouncedIds(NEAR_POPUP_SHOWN_KEY).filter((id) => currentIdSet.has(id));
            const popupShownSet = new Set(popupShown);

            list.forEach((item) => {
                if (!spokenSet.has(item.id)) {
                    spokenSet.add(item.id);
                    spoken.push(item.id);
                    this.speakNearAlarm(item.deviceName, item.distance);
                }
                if (!popupShownSet.has(item.id)) {
                    popupShownSet.add(item.id);
                    popupShown.push(item.id);
                    this.alarmPopupQueue.push({
                        key: `near-${item.id}`,
                        level: "warning",
                        title: "靠近预警",
                        message: `设备【${item.deviceName}】距围栏边界${item.distance}米，请注意`,
                    });
                }
            });
            writeAnnouncedIds(NEAR_ANNOUNCED_KEY, spoken);
            writeAnnouncedIds(NEAR_POPUP_SHOWN_KEY, popupShown);
        },

        speakNearAlarm(deviceName, distance) {
            try {
                if (!window.speechSynthesis) return;
                const utter = new SpeechSynthesisUtterance(`设备${deviceName}距围栏边界${distance}米，请注意`);
                utter.lang = "zh-CN";
                window.speechSynthesis.speak(utter);
            } catch (e) {}
        },

        // "我已知晓"：关掉当前这条，队列里还有下一条的话紧接着弹出
        ackAlarmPopup() {
            this.alarmPopupQueue.shift();
        },

        globalWs() {
            // 提前同步占位：router.afterEach 靠 window.$globalWs 是否存在来判断要不要重新建连，
            // 如果只在握手完成（setOpenEvnt）后才赋值，短时间内多次路由跳转会在握手完成前重复调用这里，
            // 建出多个并行的连接，导致同一条推送被处理多次（越界告警语音播报重复就是这个原因）
            if (window.$globalWs) return;
            let WS = new tbwebsocket();
            window.$globalWs = WS;
            WS.setLimitConnectNum(100);
            WS.webSocketUrl = wsip.globalws;
            WS.setOpenEvnt(() => {
                this.sendCode();
                this.$heartbeat = setInterval(() => {
                    this.sendCode();
                }, 2000);
                // 只在全局 WS 真正建立连接（即已登录、在非登录页）时才拉取告警语音配置，
                // 避免在登录页/退出登录后无条件请求，后台返回未登录状态会被拦截器重定向到登录页，
                // 而 App.vue 常驻挂载，重定向后会再次触发这里，造成无限刷新
                this.loadAlarmVoiceConfig();
            });
            WS.setMessageEvnt(async (messageEvnt) => {
                const code = messageEvnt.code;
                if (code == 2) {
                    this.$store.commit("setNewsDot", true);
                    if (messageEvnt.data === true) {
                        this.showSafetyNotice()
                    }
                } else if (code == 1000) {
                    await this.$store.dispatch("user/resetAll");
                    window.location.href = "/login";
                } else if (code == 3) {
                    // 越界告警：正在越界的设备列表，每 3 秒推一次；空数组代表清屏（由有告警变为无告警时会推一次空帧）
                    const list = Array.isArray(messageEvnt.data) ? messageEvnt.data : [];
                    this.$store.commit("boundaryAlarm/setState", { key: "activeCount", value: list.length });
                    this.$store.commit("boundaryAlarm/setState", { key: "activeAlarm", value: list[0] || null });
                    this.$store.commit("boundaryAlarm/setState", { key: "activeAlarms", value: list });
                    this.announceOverrunAlarms(list);
                } else if (code == 4) {
                    // 全局电子围栏发生变化：只广播一个时间戳，具体页面自己按 cpuId 调 getByCpuId 重新拉取
                    this.$store.commit("setState", { key: "globalFenceUpdatedAt", value: Date.now() });
                } else if (code == 5) {
                    // 靠近预警：当前靠近围栏边界（未越界）的设备列表，每 1 秒推一次；空数组代表清屏。
                    // 页头下方不展示（只展示越界告警），这里只负责按配置决定要不要播语音
                    const list = Array.isArray(messageEvnt.data) ? messageEvnt.data : [];
                    this.announceNearAlarms(list);
                }
            });
            WS.setOncloseEvnt(() => {
                this.clearInterval_heartbeat();
                window.$globalWs = null;
            });
            WS.setOnerrorEvnt(() => {});
            WS.init();
        },

        confirmSafetyNotice() {
            this.$axios.post('/fcms/message/setRead', { id: this.safetyNotice.id })
                .finally(() => {
                    this.safetyNoticeVisible = false
                    this.clearCountdown()
                })
        },

        startCountdown(seconds) {
            this.safetyCountdown = seconds
            this._countdownTimer = setInterval(() => {
                this.safetyCountdown--
                if (this.safetyCountdown <= 0) {
                    this.clearCountdown()
                }
            }, 1000)
        },

        clearCountdown() {
            if (this._countdownTimer) {
                clearInterval(this._countdownTimer)
                this._countdownTimer = null
            }
            this.safetyCountdown = 0
        },

        showSafetyNotice() {
            this.$axios.get('/fcms/message/getUnreadPopUp').then((res) => {
                const { state, data } = res.data
                if (state === 1 && data) {
                    this.safetyNotice.id = data.id || null
                    this.safetyNotice.title = data.title || ''
                    this.safetyNotice.content = data.content || ''
                    this.safetyNotice.popupConfirmText = data.popupConfirmText || '我已知晓'
                    this.safetyNotice.popupReadMode = data.popupReadMode || 1
                    this.safetyNotice.popupReadSeconds = data.popupReadSeconds || 0
                    this.safetyNoticeVisible = true
                    if (data.popupReadMode === 2 && data.popupReadSeconds > 0) {
                        this.startCountdown(data.popupReadSeconds)
                    }
                }
            })
        },

        sendCode() {
            if (window.$globalWs) {
                window.$globalWs.webSocket.send(
                    JSON.stringify({
                        code: 1,
                    })
                );
            }
        },

        clearInterval_heartbeat() {
            if (this.$heartbeat) {
                clearInterval(this.$heartbeat);
                this.$heartbeat = null;
            }
        },

        closeWS() {
            if (window.$globalWs) {
                window.$globalWs.setLimitConnectNum(0);
                window.$globalWs.webSocket.close();
                window.$globalWs = null;
            }
        },
    },
};
</script>

<style lang='scss'>
.scrollbar-wrapper {
    overflow-x: hidden !important;
}

.safety-notice-dialog {
    border-radius: 12px !important;
    overflow: hidden;

    .el-dialog__header {
        display: none;
    }

    .el-dialog__body {
        padding: 28px 28px 24px;
    }
}

.safety-notice {
    &__header {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 16px;

        .el-icon-warning {
            font-size: 22px;
            color: #f56c6c;
            flex-shrink: 0;
        }

        span {
            font-size: 17px;
            font-weight: 600;
            color: #1a1a1a;
        }
    }

    &__content {
        font-size: 14px;
        color: #333;
        line-height: 1.8;
        white-space: pre-line;
        margin-bottom: 16px;
    }

    &__tip {
        font-size: 13px;
        color: #999;
        margin: 0 0 20px;
    }

    &__footer {
        display: flex;
        justify-content: flex-end;

        .el-button {
            min-width: 96px;
            border-radius: 6px;
        }
    }
}

.alarm-popup-dialog {
    border-radius: 12px !important;
    overflow: hidden;

    .el-dialog__header {
        display: none;
    }

    .el-dialog__body {
        padding: 28px 28px 24px;
    }
}

.alarm-popup {
    &__header {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 16px;

        .el-icon-warning {
            font-size: 22px;
            flex-shrink: 0;
        }

        span {
            font-size: 17px;
            font-weight: 600;
            color: #1a1a1a;
        }

        &.danger .el-icon-warning {
            color: #f56c6c;
        }

        &.warning .el-icon-warning {
            color: #e6a23c;
        }
    }

    &__content {
        font-size: 14px;
        color: #333;
        line-height: 1.8;
    }

    &__more {
        margin-top: 8px;
        font-size: 12px;
        color: #909399;
    }

    &__footer {
        display: flex;
        justify-content: flex-end;
        margin-top: 20px;

        .el-button {
            min-width: 96px;
            border-radius: 6px;
        }
    }
}
</style>
